import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionTemplate } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useLoaded } from "../context/LoadContext";
import { RevealLine, RichLine } from "./Reveal";

const EASE = [0.22, 1, 0.36, 1];
const SCENES = ["/videos/roofwork", "/videos/chimney-smoke", "/videos/fireplace", "/videos/embers"];
const SCENE_MS = 7000;

const lineWords = (line) =>
  line.flatMap((seg) =>
    seg.t.trim().split(/\s+/).filter(Boolean).map((word) => ({ word, i: seg.i }))
  );

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (window.lenis && el) window.lenis.scrollTo(el, { offset: -70 });
  else el?.scrollIntoView({ behavior: "smooth" });
};

export const Hero = () => {
  const { t } = useLang();
  const ready = useLoaded();
  const [scene, setScene] = useState(0);
  const videoRefs = useRef([]);

  useEffect(() => {
    const id = setInterval(() => setScene((s) => (s + 1) % SCENES.length), SCENE_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    videoRefs.current.forEach((v, i) => {
      if (!v) return;
      if (i === scene) {
        v.currentTime = 0;
        v.play().catch(() => {});
      } else {
        setTimeout(() => { if (videoRefs.current[i]) videoRefs.current[i].pause(); }, 1600);
      }
    });
  }, [scene]);

  const wrapRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: wrapRef, offset: ["start start", "end end"] });
  const inset = useTransform(scrollYProgress, [0.05, 0.95], [0, 6]);
  const radius = useTransform(scrollYProgress, [0.05, 0.95], [0, 32]);
  const clip = useMotionTemplate`inset(${inset}% round ${radius}px)`;
  const videoScale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const contentY = useTransform(scrollYProgress, [0, 0.65], ["0%", "-26%"]);
  const contentOp = useTransform(scrollYProgress, [0.08, 0.5], [1, 0]);

  const imgRef = useRef(null);
  const imgScroll = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const imgY = useTransform(imgScroll.scrollYProgress, [0, 1], ["-12%", "12%"]);
  const imgScale = useTransform(imgScroll.scrollYProgress, [0, 0.5], [1.15, 1]);

  const lines = t.hero.lines.map(lineWords);
  const offsets = lines.map((_, idx) => lines.slice(0, idx).reduce((a, l) => a + l.length, 0));

  return (
    <section data-testid="hero-section" className="relative">
      <div ref={wrapRef} className="relative h-[175vh]">
        <div className="sticky top-0 h-screen overflow-hidden bg-[#faf9f6]">
          <motion.div style={{ clipPath: clip }} className="absolute inset-0 bg-neutral-950 overflow-hidden">
            {SCENES.map((base, i) => (
              <motion.video
                key={base}
                ref={(el) => (videoRefs.current[i] = el)}
                data-testid={`hero-video-${i}`}
                poster={`${base}.jpg`}
                autoPlay={i === 0}
                muted
                loop
                playsInline
                preload="auto"
                initial={{ opacity: i === 0 ? 1 : 0 }}
                animate={{ opacity: scene === i ? 1 : 0 }}
                transition={{ duration: 1.5, ease: "easeInOut" }}
                style={{ scale: videoScale }}
                className="absolute inset-0 w-full h-full object-cover"
              >
                <source src={`${base}.mp4`} type="video/mp4" />
                <source src={`${base}.webm`} type="video/webm" />
              </motion.video>
            ))}
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/25 to-black/70 pointer-events-none" />

            <motion.div
              style={{ y: contentY, opacity: contentOp }}
              className="relative z-10 h-full flex flex-col justify-between px-5 sm:px-10 pt-24 sm:pt-28 pb-7"
            >
              <div className="flex justify-between items-start">
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={ready ? { opacity: 1 } : {}}
                  transition={{ delay: 1.2, duration: 0.8 }}
                  className="font-tech text-[10px] sm:text-xs tracking-[0.35em] uppercase text-white/60"
                >
                  {t.hero.topLeft}
                </motion.span>
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={ready ? { opacity: 1 } : {}}
                  transition={{ delay: 1.2, duration: 0.8 }}
                  className="font-tech text-[10px] sm:text-xs tracking-[0.35em] uppercase text-white/60 text-right"
                >
                  {t.hero.topRight}
                </motion.span>
              </div>

              <h1 className="font-display font-medium tracking-tight text-white text-center text-[13vw] sm:text-[10.5vw] lg:text-[9vw] leading-[1.02] drop-shadow-lg">
                {lines.map((words, li) => (
                  <span key={li} className="flex flex-wrap justify-center gap-x-[0.26em]">
                    {words.map((w, wi) => (
                      <span key={wi} className="inline-block overflow-hidden align-bottom pb-[0.12em] -mb-[0.12em]">
                        <motion.span
                          className={`inline-block ${w.i ? "italic font-normal text-gradient-fire pr-[0.05em]" : ""}`}
                          initial={{ y: "118%" }}
                          animate={ready ? { y: 0 } : { y: "118%" }}
                          transition={{ duration: 1.15, delay: 0.08 + (offsets[li] + wi) * 0.07, ease: EASE }}
                        >
                          {w.word}
                        </motion.span>
                      </span>
                    ))}
                  </span>
                ))}
              </h1>

              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={ready ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
                className="flex flex-col sm:flex-row items-center sm:items-end justify-between gap-8"
              >
                <div className="text-center sm:text-left max-w-md">
                  <p className="text-sm sm:text-base text-white/70 leading-relaxed">{t.hero.sub}</p>
                  <div className="mt-6 flex flex-wrap items-center justify-center sm:justify-start gap-4">
                    <button
                      data-testid="hero-book-btn"
                      onClick={() => scrollToId("booking")}
                      className="btn-fire btn-slide rounded-full px-8 py-4 font-tech text-xs sm:text-sm font-semibold tracking-widest uppercase text-white"
                    >
                      <span className="label-wrap"><span>{t.hero.cta1}</span><span aria-hidden="true">{t.hero.cta1}</span></span>
                    </button>
                    <button
                      data-testid="hero-services-btn"
                      onClick={() => scrollToId("services")}
                      className="rounded-full px-8 py-4 font-tech text-xs sm:text-sm tracking-widest uppercase text-white border border-white/40 hover:border-[#ff6b00] hover:bg-[#ff6b00]/10 transition-colors duration-300"
                    >
                      {t.hero.cta2}
                    </button>
                  </div>
                </div>

                <div data-testid="hero-scene-indicator" className="w-64 shrink-0 text-center sm:text-right sm:mr-14">
                  <p className="font-tech text-[10px] tracking-[0.25em] uppercase text-white/80 whitespace-nowrap">{t.hero.scenes[scene]}</p>
                  <div className="mt-2 h-[2px] bg-white/20 overflow-hidden rounded-full">
                    <span key={scene} className="scene-progress" style={{ animationDuration: `${SCENE_MS}ms` }} />
                  </div>
                  <p className="mt-2 font-tech text-[10px] tracking-[0.3em] text-white/40">0{scene + 1} / 04</p>
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={ready ? { opacity: 1 } : {}}
              transition={{ delay: 2 }}
              style={{ opacity: contentOp }}
              className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/60"
            >
              <span className="font-tech text-[10px] tracking-[0.3em] uppercase">{t.hero.scroll}</span>
              <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
                <ArrowDown size={16} className="text-[#ff6b00]" />
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <div data-testid="deserve-statement" className="pt-16 pb-24 sm:pt-24 sm:pb-32 text-center px-5">
        <h2 className="font-display text-[9.5vw] sm:text-[6.5vw] leading-[1.08] tracking-tight text-neutral-900">
          <RevealLine><RichLine segments={t.deserve.l1} /></RevealLine>
          <RevealLine delay={0.14}>
            <span className="block font-tech font-light tracking-[0.55em] uppercase text-neutral-400 text-[3.2vw] sm:text-[1.3vw] py-3 sm:py-5">
              {t.deserve.mid}
            </span>
          </RevealLine>
          <RevealLine delay={0.22}><RichLine segments={t.deserve.l2} /></RevealLine>
        </h2>
      </div>

      <div ref={imgRef} className="max-w-6xl mx-auto px-5 sm:px-8 pb-20">
        <div className="relative overflow-hidden rounded-2xl h-[55vh] sm:h-[80vh]">
          <motion.img
            style={{ y: imgY, scale: imgScale }}
            src="https://images.unsplash.com/photo-1781344334903-f33d8b76e292?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800"
            alt="Luxury fireplace"
            className="w-full h-full object-cover"
          />
        </div>
        <p className="mt-4 text-center font-tech text-[11px] tracking-[0.3em] uppercase text-neutral-400">{t.hero.imageCaption}</p>
      </div>
    </section>
  );
};
